import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Json } from '@/integrations/supabase/types';

export interface QuestionResult {
  questionId: string;
  lessonId: string;
  type: string;
  correct: boolean;
  timestamp: string;
}

export interface UserProgress {
  xp: number;
  gems: number;
  streak: number;
  lastActiveDate: string;
  hearts: number;
  completedLessons: string[];
  lessonScores: Record<string, { correct: number; total: number }>;
  questionHistory: QuestionResult[];
  level: number;
}

const LEVELS = [
  { name: 'Beginner', tier: 'beginner', minXp: 0 },
  { name: 'Beginner II', tier: 'beginner', minXp: 75 },
  { name: 'Intermediate', tier: 'intermediate', minXp: 200 },
  { name: 'Intermediate II', tier: 'intermediate', minXp: 400 },
  { name: 'Advanced', tier: 'advanced', minXp: 700 },
  { name: 'Advanced II', tier: 'advanced', minXp: 1000 },
  { name: 'Expert', tier: 'expert', minXp: 1500 },
];

const DEFAULT_PROGRESS: UserProgress = {
  xp: 0,
  gems: 0,
  streak: 0,
  lastActiveDate: '',
  hearts: 5,
  completedLessons: [],
  lessonScores: {},
  questionHistory: [],
  level: 0,
};

function getLevel(xp: number): number {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].minXp) return i;
  }
  return 0;
}

export function getLevelName(level: number): string {
  return LEVELS[level]?.name ?? LEVELS[0].name;
}

export function getNextLevelXp(level: number): number {
  return LEVELS[level + 1]?.minXp ?? LEVELS[LEVELS.length - 1].minXp;
}

export function getCurrentLevelXp(level: number): number {
  return LEVELS[level]?.minXp ?? 0;
}

function dbToProgress(row: any): UserProgress {
  const scores = (row.lesson_scores && typeof row.lesson_scores === 'object' && !Array.isArray(row.lesson_scores))
    ? row.lesson_scores as Record<string, { correct: number; total: number }>
    : {};
  const history = Array.isArray(row.question_history) ? row.question_history as QuestionResult[] : [];
  return {
    xp: row.xp ?? 0,
    gems: row.gems ?? 0,
    streak: row.streak ?? 0,
    lastActiveDate: row.last_active_date ?? '',
    hearts: row.hearts ?? 5,
    completedLessons: row.completed_lessons ?? [],
    lessonScores: scores,
    questionHistory: history,
    level: row.level ?? 0,
  };
}

export interface WeakArea {
  type: string;
  label: string;
  emoji: string;
  totalAttempts: number;
  wrongCount: number;
  accuracy: number;
  recentWrongQuestionIds: string[];
}

const TYPE_META: Record<string, { label: string; emoji: string }> = {
  'multiple-choice': { label: 'Multiple Choice', emoji: '❓' },
  'identify': { label: 'Play Identification', emoji: '🔍' },
  'fix-mistake': { label: 'Fix the Mistake', emoji: '🚩' },
  'drag-drop': { label: 'Formation Building', emoji: '🧩' },
  'diagram-identify': { label: 'Diagram Reading', emoji: '📊' },
  'true-false': { label: 'True / False', emoji: '✅' },
  'scenario': { label: 'Scenarios', emoji: '🎬' },
  'situational': { label: 'Situational Calls', emoji: '🎯' },
  'matching': { label: 'Matching Pairs', emoji: '🔗' },
};


export function computeWeakAreas(history: QuestionResult[]): WeakArea[] {
  const byType: Record<string, { total: number; wrong: number; recentWrong: string[] }> = {};

  for (const q of history) {
    if (!byType[q.type]) byType[q.type] = { total: 0, wrong: 0, recentWrong: [] };
    byType[q.type].total++;
    if (!q.correct) {
      byType[q.type].wrong++;
      if (byType[q.type].recentWrong.length < 5) {
        byType[q.type].recentWrong.push(q.questionId);
      }
    }
  }

  return Object.entries(byType)
    .map(([type, data]) => ({
      type,
      label: TYPE_META[type]?.label ?? type,
      emoji: TYPE_META[type]?.emoji ?? '📝',
      totalAttempts: data.total,
      wrongCount: data.wrong,
      accuracy: data.total > 0 ? Math.round((1 - data.wrong / data.total) * 100) : 100,
      recentWrongQuestionIds: data.recentWrong,
    }))
    .filter(a => a.totalAttempts >= 2) // need at least 2 attempts to show
    .sort((a, b) => a.accuracy - b.accuracy); // worst first
}

export function useProgress() {
  const { user } = useAuth();
  const [progress, setProgress] = useState<UserProgress>({ ...DEFAULT_PROGRESS });
  const [loaded, setLoaded] = useState(false);
  // Track the raw DB hearts value so regen math stays correct
  const [dbHeartsValue, setDbHeartsValue] = useState(5);

  // Calculate hearts with time-based regeneration from the raw DB value
  const computeHeartsWithRegen = useCallback((rawDbHearts: number, dbUpdatedAt?: string): number => {
    if (rawDbHearts >= 5) return 5;
    const lastLostStr = localStorage.getItem('hearts_last_lost_at');
    // Use localStorage timestamp if available, otherwise fall back to DB updated_at
    const referenceTime = lastLostStr
      ? parseInt(lastLostStr, 10)
      : dbUpdatedAt
        ? new Date(dbUpdatedAt).getTime()
        : null;
    if (!referenceTime) return rawDbHearts;
    const elapsed = Date.now() - referenceTime;
    const regenCount = Math.floor(elapsed / (60 * 60 * 1000)); // 1 per hour
    const result = Math.min(5, rawDbHearts + regenCount);
    return result;
  }, []);

  // Persist regenerated hearts to DB so they survive reloads/device switches.
  // IMPORTANT: only call `refill_hearts` (which slams DB to 5) when fully refilled.
  // For partial regen, update the row's hearts column to the actual value.
  const persistRegenHearts = useCallback(async (newHearts: number) => {
    if (!user) return;
    if (newHearts >= 5) {
      localStorage.removeItem('hearts_last_lost_at');
      await supabase.rpc('refill_hearts');
      return;
    }
    await supabase.rpc('set_partial_hearts' as any, { p_hearts: newHearts });
  }, [user]);

  // Load from DB on login
  useEffect(() => {
    if (!user) {
      setProgress({ ...DEFAULT_PROGRESS });
      setDbHeartsValue(5);
      setLoaded(false);
      return;
    }

    // Never let one account's numbers render under another account. Any time
    // the signed-in user changes we wipe in-memory progress first, then fetch.
    let cancelled = false;
    setProgress({ ...DEFAULT_PROGRESS });
    setDbHeartsValue(5);
    setLoaded(false);

    const load = async () => {
      const { data } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (cancelled) return;

      if (data) {
        const p = dbToProgress(data);
        const rawDbHearts = p.hearts;
        setDbHeartsValue(rawDbHearts);

        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        if (p.lastActiveDate !== today && p.lastActiveDate !== yesterday) {
          p.streak = 0;
        }
        const regenHearts = computeHeartsWithRegen(rawDbHearts, data.updated_at);
        p.hearts = regenHearts;
        setProgress(p);

        // If regen produced more hearts, persist to DB
        if (regenHearts > rawDbHearts) {
          setDbHeartsValue(regenHearts);
          await persistRegenHearts(regenHearts);
        }
      }
      if (!cancelled) setLoaded(true);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, computeHeartsWithRegen, persistRegenHearts]);


  // Snapshot current hearts to localStorage so other views (e.g. LessonView)
  // can detect UI divergence between the Home heart count and what they receive.
  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(
        'hearts_snapshot',
        JSON.stringify({ value: progress.hearts, ts: Date.now() })
      );
    } catch {}
  }, [loaded, progress.hearts]);

  // Periodic heart regen check (every 60s)
  useEffect(() => {
    if (!loaded || progress.hearts >= 5) return;
    const interval = setInterval(async () => {
      const newHearts = computeHeartsWithRegen(dbHeartsValue, undefined);
      if (newHearts > progress.hearts) {
        setProgress(prev => ({ ...prev, hearts: newHearts }));
        if (newHearts >= 5) {
          setDbHeartsValue(5);
          await persistRegenHearts(5);
        }
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [loaded, progress.hearts, dbHeartsValue, computeHeartsWithRegen, persistRegenHearts]);

  // All mutations go through server-side RPCs for security

  const reloadProgress = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_progress')
      .select('*')
      .eq('user_id', user.id)
      .single();
    if (data) {
      const p = dbToProgress(data);
      const rawDbHearts = p.hearts;
      setDbHeartsValue(rawDbHearts);
      // Apply regen on reload too
      p.hearts = computeHeartsWithRegen(rawDbHearts, data.updated_at);
      setProgress(p);
    }
  }, [user, computeHeartsWithRegen]);

  const completeLesson = useCallback(async (lessonId: string, correct: number, total: number, xpReward: number, questionResultsBatch?: QuestionResult[]) => {
    // Refill hearts on lesson completion
    localStorage.removeItem('hearts_last_lost_at');

    // Optimistic local update
    setProgress(prev => {
      if (prev.completedLessons.includes(lessonId)) {
        const newHistory = questionResultsBatch
          ? [...prev.questionHistory, ...questionResultsBatch].slice(-200)
          : prev.questionHistory;
        return { ...prev, hearts: 5, lessonScores: { ...prev.lessonScores, [lessonId]: { correct, total } }, questionHistory: newHistory };
      }
      const today = new Date().toISOString().split('T')[0];
      const newXp = prev.xp + xpReward;
      const gemsEarned = 5 + (correct === total ? 5 : 0);
      const newHistory = questionResultsBatch
        ? [...prev.questionHistory, ...questionResultsBatch].slice(-200)
        : prev.questionHistory;
      return {
        ...prev,
        xp: newXp,
        gems: prev.gems + gemsEarned,
        hearts: 5,
        streak: prev.lastActiveDate === today ? prev.streak : prev.streak + 1,
        lastActiveDate: today,
        completedLessons: [...prev.completedLessons, lessonId],
        lessonScores: { ...prev.lessonScores, [lessonId]: { correct, total } },
        questionHistory: newHistory,
        level: getLevel(newXp),
      };
    });

    // Server-side RPC
    await supabase.rpc('record_lesson_completion', {
      p_lesson_id: lessonId,
      p_correct: correct,
      p_total: total,
      p_xp_reward: xpReward,
      p_question_results: (questionResultsBatch ?? []) as unknown as Json,
    });
  }, []);

  const recordQuestionResults = useCallback(async (results: QuestionResult[]) => {
    // Question results are recorded as part of lesson completion RPC
    // This is kept for local state only
    setProgress(prev => {
      const newHistory = [...prev.questionHistory, ...results].slice(-200);
      return { ...prev, questionHistory: newHistory };
    });
  }, []);

  const loseHeart = useCallback(async () => {
    localStorage.setItem('hearts_last_lost_at', Date.now().toString());
    setProgress(prev => {
      const newHearts = Math.max(0, prev.hearts - 1);
      setDbHeartsValue(newHearts);
      return { ...prev, hearts: newHearts };
    });
    await supabase.rpc('lose_heart');
  }, []);

  const refillHearts = useCallback(async () => {
    localStorage.removeItem('hearts_last_lost_at');
    setDbHeartsValue(5);
    setProgress(prev => ({ ...prev, hearts: 5 }));
    await supabase.rpc('refill_hearts');
  }, []);

  const REFILL_GEM_COST = 50;
  const refillHeartsWithGems = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    if (!user) return { ok: false, reason: 'not-authenticated' };
    if (progress.hearts >= 5) return { ok: false, reason: 'already-full' };
    if (progress.gems < REFILL_GEM_COST) return { ok: false, reason: 'not-enough-gems' };

    const newGems = progress.gems - REFILL_GEM_COST;
    localStorage.removeItem('hearts_last_lost_at');
    setDbHeartsValue(5);
    setProgress(prev => ({ ...prev, hearts: 5, gems: newGems }));

    const { data, error } = await supabase.rpc('spend_gems_refill_hearts' as any, { p_cost: REFILL_GEM_COST });

    if (error || !(data as any)?.ok) {
      // Roll back optimistic update
      setProgress(prev => ({ ...prev, gems: progress.gems }));
      return { ok: false, reason: (data as any)?.reason || error?.message };
    }
    return { ok: true };
  }, [user, progress.hearts, progress.gems]);

  const restoreOneHeart = useCallback(async () => {
    setProgress(prev => ({ ...prev, hearts: Math.min(5, prev.hearts + 1) }));
    await supabase.rpc('refill_hearts');
  }, []);

  const completeChallenge = useCallback(async (challengeId: string, correct: boolean, xpReward: number) => {
    setProgress(prev => {
      if (prev.completedLessons.includes(challengeId)) return prev;
      const today = new Date().toISOString().split('T')[0];
      const newXp = prev.xp + xpReward;
      return {
        ...prev,
        xp: newXp,
        streak: prev.lastActiveDate === today ? prev.streak : prev.streak + 1,
        lastActiveDate: today,
        completedLessons: [...prev.completedLessons, challengeId],
        level: getLevel(newXp),
      };
    });
    await supabase.rpc('complete_challenge', { p_challenge_id: challengeId, p_xp_reward: xpReward });
  }, []);

  // Server-validated streak milestone claim. Server returns the actual reward
  // amounts (clients can no longer pick arbitrary XP/gems).
  const claimStreakMilestone = useCallback(async (days: number): Promise<{ ok: boolean; xp?: number; gems?: number; reason?: string }> => {
    const { data, error } = await supabase.rpc('claim_streak_milestone' as any, { p_days: days });
    if (error) return { ok: false, reason: error.message };
    const reward = (data ?? {}) as { xp?: number; gems?: number };
    const xpAwarded = reward.xp ?? 0;
    const gemsAwarded = reward.gems ?? 0;
    setProgress(prev => {
      const newXp = prev.xp + xpAwarded;
      return { ...prev, xp: newXp, gems: prev.gems + gemsAwarded, level: getLevel(newXp) };
    });
    return { ok: true, xp: xpAwarded, gems: gemsAwarded };
  }, []);

  const weakAreas = computeWeakAreas(progress.questionHistory);

  const accuracy = Object.values(progress.lessonScores).reduce(
    (acc, s) => ({ correct: acc.correct + s.correct, total: acc.total + s.total }),
    { correct: 0, total: 0 }
  );

  return {
    progress,
    completeLesson,
    completeChallenge,
    recordQuestionResults,
    loseHeart,
    refillHearts,
    refillHeartsWithGems,
    restoreOneHeart,
    claimStreakMilestone,
    weakAreas,
    accuracy: accuracy.total > 0 ? Math.round((accuracy.correct / accuracy.total) * 100) : 0,
    levelName: getLevelName(progress.level),
    nextLevelXp: getNextLevelXp(progress.level),
    currentLevelXp: getCurrentLevelXp(progress.level),
    loaded,
  };
}
